<template>
<section>
  <b-loading :active="loading" v-if="loading"/>
  <div v-if="user" class="section">
    <section class="hero is-primary">
      <div class="hero-body columns">
        <div class="column is-one-quarter image-container">
          <img class="profile-image" :src="user.profile.image ? user.profile.image.file : null" />
        </div>
        <div class="column">
          <div class="username">
            <p class="is-size-3">{{userName}}</p>
            <div class="username-hidden-area">
              <HiddenArea  v-if="!canSeePrivateContent"/>
            </div>
          </div>
          <p class="is-size-5">Type: {{user.type}}</p>
          <p class="is-size-5">Member since: {{$moment(user.created).format('MMMM Do YYYY')}}</p>
          <p class="is-size-5">ID: #{{user.id}}</p>
          <div v-if="user.type !== 'charterer' && user.type !== 'owner'">
            <div class="rating-container">
              <b>Ratings ({{user.rating_count}} Ratings)</b>
              <button class="button is-primary is-inverted is-small"
                v-if="canRate"
                @click="isRatingModalActive=true">
                Rate!
              </button>
              <b-modal :active.sync="isRatingModalActive" has-modal-card>
                <UserRatingForm @rating_saved="updateRating" :rating="rating" :type="user.type"/>
              </b-modal>
            </div>

            <div class="rating-container"
              v-if="['on_dock', 'crew', 'diver'].indexOf(user.type) !== -1">
              <p>Expertise:&nbsp;&nbsp;&nbsp;</p>
              <StarRating :star-size="20" :show-rating="false"
                :rating="user.average_expertise_rating"
                :read-only="true" :increment="0.1"/>
            </div>

            <div class="rating-container">
              <p>Communication:&nbsp;&nbsp;&nbsp;</p>
              <StarRating :star-size="20" :show-rating="false"
                :rating="user.average_communication_rating"
                :read-only="true" :increment="0.1"/>
            </div>

            <div class="rating-container">
              <p>Trust:&nbsp;&nbsp;&nbsp;</p>
              <StarRating :star-size="20" :show-rating="false"
                :rating="user.average_trust_rating"
                :read-only="true" :increment="0.1"/>
            </div>

            <div class="rating-container">
              <p>Consistency:&nbsp;&nbsp;&nbsp;</p>
              <StarRating :star-size="20" :show-rating="false"
                :rating="user.average_consistency_rating"
                :read-only="true" :increment="0.1"/>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="top-margin">
      <div class="columns">
        <div class="column is-one-quarter">
          <h2 class="is-size-4">Contact</h2>
          <template v-if="canSeePrivateContent">
            <p>Email: <strong>{{user.email}}</strong></p>
            <p>Phone number: <strong>{{user.profile.phone_number}}</strong></p>
            <p>Whatsapp: <strong>{{user.profile.whatsapp_number}}</strong></p>
            <p>Skype ID: <strong>{{user.profile.skype_id}}</strong></p>
          </template>
          <HiddenArea v-else />
          <hr/>
          <h2 class="is-size-4">Info</h2>
          <p>Gender: <strong>{{user.profile.gender}}</strong></p>
          <template v-if="user.type === 'crew'">
            <p>Smoker: <strong>{{boolToString(user.profile.smoker)}}</strong></p>
            <p>Tatoos: <strong>{{boolToString(user.profile.visible_tatoos)}}</strong></p>
            <p>Marital status: <strong>{{user.profile.marital_status}}</strong></p>
          </template>
          <p>Current country: <strong>{{user.profile.current_country}}</strong></p>
          <p>Payment: <strong>{{user.profile.salary | money}}</strong></p>
          <p>Harbor: <strong>{{user.profile.current_harbor}}</strong></p>
          <p>Birthday:
            <strong  v-if="user.profile.birthday">
              {{user.profile.birthday}}
              ({{ $moment().diff(user.profile.birthday, 'years') }})
            </strong>
          </p>
          <p>Nationality: <strong>{{user.profile.nationality}}</strong></p>

          <template v-if="user.type==='owner'">
            <hr/>
            <h2 class="is-size-4">Company Info</h2>
            <p>Operation Type: <strong>{{user.profile.company_type}}</strong></p>
            <template v-if="user.profile.company_type !== 'private'">
              <p>Company Name: <strong>{{user.profile.company_name}}</strong></p>
              <p>Company Address: <strong>{{user.profile.company_address}}</strong></p>
              <p>Company Position: <strong>{{user.profile.company_position}}</strong></p>
            </template>
          </template>

          <template v-if="userOfTypes(['crew', 'on_dock', 'diver'])">
            <hr/>
            <h2 class="is-size-4">Desired employment</h2>
            <p>Positions:
              <strong>{{user.profile.desired_employment_positions.join(', ')}}</strong>
            </p>
            <p>Countries:
              <strong>{{user.profile.employment_countries.join(', ')}}</strong>
            </p>
            <p>Availability: <strong>{{user.profile.employment_availability}}</strong></p>
          </template>


          <template v-if="userOfTypes(['crew'])">
            <hr/>
            <h2 class="is-size-4">Passports - Visas</h2>
            <template v-if="canSeePrivateContent">
              <p>Visas: <strong>{{user.profile.visas.join(', ')}}</strong></p>
              <p>Passports:
                <strong>
                  {{user.profile.passports.map(
                    p => `${p.country} (until ${p.valid_to})`
                  ).join(', ')}}
                </strong>
              </p>
            </template>
            <HiddenArea v-else />
          </template>


          <template v-if="userOfTypes(['crew', 'diver', 'on_dock', 'charterer'])">
            <hr/>
            <h2 class="is-size-4">Experience</h2>
            <p v-if="this.user.profile.experience_years">Experience:
              <strong>
                {{`${this.user.profile.experience_years} years`}}
              </strong>
            <p>
            <p v-if="this.user.profile.yachting_experience_years">Yachting experience:
              <strong>
                {{`${this.user.profile.yachting_experience_years} years`}}
              </strong>
            <p>

            <p v-if="this.user.profile.chef_experience_years">Chef experience:
              <strong>
                {{`${this.user.profile.chef_experience_years} years`}}
              </strong>
            <p>

            <p v-if="this.user.profile.diving_experience_years">Diving experience:
              <strong>
                {{`${this.user.profile.diving_experience_years} years`}}
              </strong>
            <p>

            <p v-if="this.user.profile.on_dock_experience_years">Dock services experience:
              <strong>
                {{`${this.user.profile.on_dock_experience_years} years`}}
              </strong>
            </p>
          </template>

          <template v-if="userOfTypes(['crew', 'on_dock'])">
            <hr/>
            <h2 class="is-size-4">CV</h2>
            <p v-if="canSeePrivateContent && user.profile.cv">
              <SafeFileLink :link="user.profile.cv.file" />
            </p>
            <p v-else-if="canSeePrivateContent">No CV uploaded</p>
            <HiddenArea v-else />
            <hr/>
          </template>

          <template v-if="userOfTypes(['crew'])">
            <h2 class="is-size-4">Basic training</h2>
            <p>STCW95: <strong>{{
              user.profile.stcw95_training_expiry ?
                `Yes (until ${user.profile.stcw95_training_expiry})` :
                'No'
            }}</strong></p>
            <p>Eng 1 Medicine: <strong>{{
              user.profile.eng_1_medical_certificate_expiry ?
                `Yes (until ${user.profile.eng_1_medical_certificate_expiry})` :
                'No'
            }}</strong></p>
            <p></p>
          </template>
        </div>
        <div class="column is-three-quarters">
          <h2 class="is-size-4">About me</h2>
          <span class="wrap-oveflow">
            {{user.profile.intro}}
          </span>
          <template v-for="[category, skills] in
            Object.entries(groupedSkills).filter(([_, s]) => s.length)">
            <hr :key="`${category}-hr`"/>
            <h2 class="is-size-4" :key="category">{{category}}</h2>
            <div :class="`level-bar level-${s.level}`" :key="s.name" v-for="s in skills">
              {{s.name}}
            </div>
          </template>

          <template v-if="userOfTypes(['owner', 'charterer'])">
            <hr/>
            <h2 class="is-size-4">Vessels</h2>
            <b-taglist class="taglist-top-margin">
              <b-tag v-for="(v, index) in user.profile.vessels" rounded :key="index">
                {{v.ownership_type}} Vessel, {{v.type}}, {{v.sub_type}},
                {{v.size_ft}}ft ({{$toMeters(v.size_ft)}}m)
              </b-tag>
            </b-taglist>
          </template>

          <template v-if="userOfTypes(['charterer'])">
            <hr/>
            <h2 class="is-size-4">Charter Info</h2>
            <b-taglist class="taglist-top-margin">
              <b-tag v-for="(d, index) in user.profile.charterer_dates" rounded :key="index">
                From: {{d.start}}, To: {{d.end}}
              </b-tag>
            </b-taglist>
          </template>

          <template v-if="!userOfTypes(['owner', 'charterer'])">
            <hr/>
            <h2 class="is-size-4">Qualifications</h2>
            <b-taglist class="taglist-top-margin">
              <b-tag v-for="q in user.profile.qualifications" rounded :key="q">
                {{q}}
              </b-tag>
            </b-taglist>
          </template>

          <template v-if="!userOfTypes(['owner', 'charterer', 'on_dock'])">
            <hr />
            <h2 class="is-size-4">Certificates</h2>
            <b-taglist class="taglist-top-margin" v-if="canSeePrivateContent">
              <b-tag v-for="c in user.profile.certificates" rounded :key="c.title" size="large">
                {{c.title}},
                Issued at: {{c.issued_date}},
                By: {{c.issued_by || 'N/A'}}
                <SafeFileLink :link="c.file.file" />
              </b-tag>
            </b-taglist>
            <HiddenArea v-else />
          </template>


          <template v-if="!userOfTypes(['owner', 'charterer', 'on_dock'])">
            <hr />
            <h2 class="is-size-4">Detailed Experience</h2>
            <template >
              <div v-for="e in user.profile.experience_items" :key="e.name"
                :class="`level-bar level-${e.level}`">
                {{e.name}}
              </div>
            </template>
          </template>

          <template v-if="userOfTypes(['crew'])">
            <hr />
            <h2 class="is-size-4">Diplomas/Licences</h2>
            <b-taglist class="taglist-top-margin" v-if="canSeePrivateContent">
              <b-tag v-for="(d, index) in user.profile.diplomas" rounded :key="index" size="large">
                {{d.name}},
                <SafeFileLink :link="d.file.file" />
              </b-tag>
            </b-taglist>
            <HiddenArea v-else />
          </template>

        </div>
      </div>
    </section>
  </div>

</section>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import StarRating from 'vue-star-rating';
import HiddenArea from '@/components/HiddenArea';
import UserRatingForm from '@/views/common/UserRatingForm';
import SafeFileLink from '@/components/SafeFileLink';

export default {
  components: {
    StarRating,
    HiddenArea,
    UserRatingForm,
    SafeFileLink,
  },
  data() {
    return {
      user: null,
      rating: null,
      isRatingModalActive: false,
      loading: true,
    };
  },
  computed: {
    ...mapState({
      skills: state => state.content.skills,
      currentUser: state => state.auth.user,
    }),
    ...mapGetters({
      skillToCategory: 'content/skillToCategory',
      isLoggedInEmployer: 'auth/isLoggedInEmployer',
      isLoggedIn: 'auth/isLoggedIn',
      canSeePrivateContent: 'auth/canSeePrivateContent',
    }),
    userName() {
      return [
        this.user.profile.first_name,
        this.user.profile.middle_name,
        this.user.profile.last_name,
      ].filter(i => !!i).join(' ') || 'Anonymous';
    },
    groupedSkills() {
      return this.user.profile.skills.reduce((acc, s) => {
        const category = this.skillToCategory[s.name] || 'Other';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(s);
        return acc;
      }, {});
    },
    canRate() {
      if (!this.currentUser) return false;
      if (this.user.id === this.currentUser.id) return false;
      if (this.currentUser.type === 'owner') return true;
      if (this.currentUser.profile.desired_employment_positions.indexOf('Skipper') === -1
       && this.currentUser.profile.desired_employment_positions.indexOf('Captain') === -1) return false;
      if (this.user.type === 'diver' || this.user.type === 'on_dock') return true;
      return false;
    },
  },
  methods: {
    userOfTypes(types) {
      return types.indexOf(this.user.type) !== -1;
    },
    boolToString(bool) {
      if (bool === true) return 'Yes';
      if (bool === false) return 'No';
      return '';
    },
    async updateRating(scores) {
      this.loading = true;
      if (this.rating) {
        await this.$http.delete(`ratings/${this.rating.id}`);
      }
      this.rating = (await this.$http.post('ratings/', {
        rater: this.currentUser.id,
        ratee: this.user.id,
        ...scores,
      })).data;
      this.user = (await this.$http.get(`users/${this.$route.params.id}/`)).data;
      this.isRatingModalActive = false;
      this.loading = false;
    },
  },
  async created() {
    try {
      const [{ data: user }, ratingResponse] = await Promise.all([
        this.$http.get(`users/${this.$route.params.id}/`),
        this.isLoggedInEmployer ? this.$http.get(`ratings/?ratee=${this.$route.params.id}`) : Promise.resolve(),
      ]);
      this.user = user;
      if (ratingResponse) {
        const { data: { results: [rating] } } = ratingResponse;
        this.rating = rating;
      }
    } catch (e) {
      this.$router.replace({ name: 'not-found' });
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.profile-image {
  max-height: 10rem;
}
.image-container {
  justify-content: center;
  display: flex;
}
.top-margin {
  margin-top: 2rem;
}
.level-bar {
  border-radius: 1rem;
  padding: 0.1rem 0 0.1rem 1rem;
  margin-top: 0.1rem;
}

.level-1 {
  background-color: red;
  width: 25%;
  color: white;
}

.level-2 {
  background-color: orange;
  width: 50%;
  color: white;
}

.level-3 {
  background-color: yellow;
  width: 75%;
}

.level-4 {
  background-color: green;
  width: 100%;
  color: white;
}

hr {
  margin: 0.5rem;
}

.taglist-top-margin {
  margin-top: 1rem;
}

.wrap-oveflow {
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.rating-container {
  max-width: 400px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.username {
  display: flex;
  flex-direction: row;
}

.username-hidden-area {
 margin-left: 1rem;
}
</style>
